import { CustomFieldsApi } from "@simbelapp/order-sdk";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { CustomFieldCategoryEnum } from "~/utils/enums/custom-fields.enums";
import type { CustomField, CustomFieldsStore } from "~/utils/interfaces/custom-fields-interfaces";

export const useCustomFieldsStore = defineStore("customFieldsStore", {
  state: (): CustomFieldsStore => ({
    customFields: [],
    isLoading: false,
    error: null,
  }),

  getters: {
    hasMaxFields: (state) => {
      return state.customFields.length >= 16;
    },
  },

  actions: {
    async fetchCustomFields() {
      this.isLoading = true;
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CustomFieldsApi);
      try {
        const response = await apiInstance.getCustomFields();
        this.customFields = response;
      } catch {
        feedback.error("Erreur lors du chargement des champs personnalisés", "small");
      } finally {
        this.isLoading = false;
      }
    },

    async createCustomField(fieldData: Omit<CustomField, "customFieldId">) {
      this.isLoading = true;
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CustomFieldsApi);
        await apiInstance.createCustomField({
          createCustomFieldDto: {
            name: fieldData.name,
            category: CustomFieldCategoryEnum.USER,
          },
        });

        feedback.success("Champ personnalisé créé avec succès", "small");
      } catch {
        feedback.error("Erreur lors de la création du champ personnalisé", "small");
      } finally {
        this.isLoading = false;
      }
    },

    async updateCustomField(fieldData: CustomField) {
      if (!fieldData.customFieldId) {
        throw new Error("ID du champ requis pour la mise à jour");
      }

      this.isLoading = true;
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CustomFieldsApi);
        await apiInstance.updateCustomField({
          id: fieldData.customFieldId,
          updateCustomFieldDto: {
            name: fieldData.name,
          },
        });

        feedback.success("Champ personnalisé mis à jour avec succès", "small");
      } catch {
        feedback.error("Erreur lors de la mise à jour du champ personnalisé", "small");
      } finally {
        this.isLoading = false;
      }
    },

    async deleteCustomField(fieldId: string) {
      const feedback = useFeedback();
      this.isLoading = true;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CustomFieldsApi);
        await apiInstance.removeCustomField({
          id: fieldId,
        });

        feedback.success("Champ personnalisé supprimé avec succès", "small");
      } catch (error) {
        feedback.error("Erreur lors de la suppression du champ personnalisé", "small");
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
